import { useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { PartyPopper, MessageCircle, Trophy } from 'lucide-react'
import { APP_CONSTANTS } from '@/lib/constants/app'
import { CoordinatorList } from '@/components/registration/CoordinatorList'
import { useRegistrationStore } from '@/lib/store/registrationStore'
import { useEffect } from 'react'

export function SuccessPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const isPickleballEnrolled = location.state?.isPickleballEnrolled || false
  const resetStore = useRegistrationStore((state) => state.reset)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleGoBack = () => {
    resetStore()
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Card variant="premium" className="overflow-hidden">
          <div className="p-6 sm:p-8 space-y-8">
            {/* Success Message */}
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                  <PartyPopper className="w-10 h-10 text-amber-600" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  Congratulations!
                </h1>
                <p className="text-slate-600 max-w-md mx-auto">
                  Your registration for the most coveted sporting event of Whispering Palms is now complete.
                </p>
              </div>
            </div>

            {/* Coordinator List */}
            <CoordinatorList />

            {/* WhatsApp Community Cards */}
            <div className="space-y-4">
              {/* Main WhatsApp Community */}
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-50 to-green-100 border border-green-200 p-5 shadow-sm">
                {/* Decorative gradient orb */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-400/10 rounded-full blur-3xl" />

                <div className="relative">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                      <MessageCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-bold text-green-900 text-base">
                        Official WhatsApp Community
                      </h3>
                      <p className="text-sm text-green-700 mt-1">
                        Stay connected — updates, challenges and surprises ahead.
                      </p>
                    </div>
                  </div>

                  <a
                    href={APP_CONSTANTS.WHATSAPP_COMMUNITY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                      Join Community
                    </Button>
                  </a>
                </div>
              </div>

              {/* Pickleball Community (Conditional) */}
              {isPickleballEnrolled && (
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-5 shadow-sm">
                  {/* Decorative gradient orb */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-400/10 rounded-full blur-3xl" />

                  <div className="relative">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Trophy className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1 pt-1">
                        <h3 className="font-bold text-blue-900 text-base">
                          Pickleball Community
                        </h3>
                        <p className="text-sm text-blue-700 mt-1">
                          Connect with other pickleball players and stay updated.
                        </p>
                      </div>
                    </div>

                    <a
                      href={APP_CONSTANTS.WHATSAPP_PICKLEBALL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold">
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                        Join Pickleball Group
                      </Button>
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Go Back Button */}
            <Button
              variant="outline"
              onClick={handleGoBack}
              className="w-full"
            >
              Register Another Player
            </Button>
          </div>
        </Card>
      </div>
    </div>
  )
}
